import { useCallback, useMemo, useState } from "react";
import { useNavigate } from 'react-router';
import {MapMarkerContentContainer } from '../elements/marker-content/MapMarkerContentContainer';
import { Circle } from "phosphor-react-sc";
import { useSelectedLocation, useSetShowInfo, useSetSelectedLocation, useHasQuayManualAccess, useShowLocationQuayManual } from '../../hooks/useMapStore';
import {
    AdvancedMarker,
} from '@vis.gl/react-google-maps';
import { isMobile } from 'components/helpers/DeviceHelpers';
import MapApiRepository from '../../repositories/MapApiRepository';

interface MapCertificateLocationMarkerProps {
    marker: any,
}

export const MapCertificateLocationMarker = ({
    marker,
}: MapCertificateLocationMarkerProps) => {

    const selectedLocation = useSelectedLocation();
    const setSelectedLocation = useSetSelectedLocation();
    const setShowInfo = useSetShowInfo();
    const { hasQuayManualAccess } = useHasQuayManualAccess();
    const showLocationQuayManual = useShowLocationQuayManual();

    const navigate = useNavigate();
    const mobile = isMobile();

    const isSelected = selectedLocation?.id === marker.id;

    const [isHighlighted, setIsHighlighted] = useState<boolean>(false);

    const zIndex = useMemo(() => {
        return marker?.topmost ? 1 : 0;
    }, [marker?.topmost]);

    const onClick = useCallback(async () => {
        if (!mobile) {
            if (!hasQuayManualAccess || !showLocationQuayManual) return;
            const response = await MapApiRepository.getQuayManualByLocationAndProximity(marker.id);
            if (response.ok) {
                const result = await response.json();
                if (result.length > 0) {
                    navigate(`/quaymanuals/${marker.id}/proximity?title=${encodeURI(marker.description)}`);
                }
            }
        } else {

            if (!isSelected) {
                setSelectedLocation({ id: marker.id });
            }
            setShowInfo(marker);
        }
    }, [isSelected, marker, setSelectedLocation, setShowInfo, hasQuayManualAccess, showLocationQuayManual]);

    // Hover only affects visual emphasis when not already selected
    const onMouseEnter = useCallback(() => {
        // MapMarkerContentContainer handles hover styling; no state needed
        setIsHighlighted(true);

        if (mobile) return;
        setShowInfo(marker);
    }, [setIsHighlighted, setShowInfo, marker, mobile]);

    const onMouseLeave = useCallback(() => {
        // Keep selection visual state; no state toggles required
        setIsHighlighted(false);

        if (mobile) return;
        setShowInfo(null);
    }, [setIsHighlighted, setShowInfo, mobile]);

    return (
        <AdvancedMarker zIndex={zIndex} position={marker.position} onClick={onClick}>
            <MapMarkerContentContainer
                style={{ marginBottom: -10 }}
                isSelected={isSelected || isHighlighted}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}>
                <Circle size={25} weight="fill" color="var(--map-pilot-certificate-location-bg)" className="svg-icon" />
            </MapMarkerContentContainer>
        </AdvancedMarker>
    );
}
